import React, { useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureUser } from '../../util/data';

import { Avatar, ReviewRating, UserDisplayName, ReviewResponse, ReviewResponseForm } from '../../components';

import css from './Reviews.module.css';

const Review = props => {
  const { review, intl, listing, currentUser, onResponseSubmit } = props;
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseSubmitInProgress, setResponseSubmitInProgress] = useState(false);

  const date = review.attributes.createdAt;
  const dateString = intl.formatDate(date, { month: 'long', year: 'numeric' });

  const reviewId = review.id.uuid;
  const listingProvider = ensureUser(listing?.author);
  const isProvider = currentUser?.id?.uuid === listingProvider?.id?.uuid;

  // Get existing response from listing publicData
  const reviewResponses = listing?.attributes?.publicData?.reviewResponses || {};
  const existingResponse = reviewResponses[reviewId];
  const hasResponse = !!existingResponse;

  const handleResponseSubmit = async (responseText) => {
    if (onResponseSubmit) {
      setResponseSubmitInProgress(true);
      try {
        await onResponseSubmit(reviewId, responseText);
        setShowResponseForm(false);
      } catch (error) {
        console.error('Failed to submit response:', error);
      } finally {
        setResponseSubmitInProgress(false);
      }
    }
  };

  return (
    <div className={css.review}>
      <Avatar className={css.avatar} user={review.author} />
      <div className={css.reviewContainer}>
        <ReviewRating
          rating={review.attributes.rating}
          className={css.mobileReviewRating}
          reviewStarClassName={css.reviewRatingStar}
        />
        <p className={css.reviewContent}>{review.attributes.content}</p>
        <p className={css.reviewInfo}>
          <UserDisplayName user={review.author} intl={intl} />
          <span className={css.separator}>•</span>
          {dateString}
          <span className={css.desktopSeparator}>•</span>
          <span className={css.desktopReviewRatingWrapper}>
            <ReviewRating
              rating={review.attributes.rating}
              className={css.desktopReviewRating}
              reviewStarClassName={css.reviewRatingStar}
            />
          </span>
        </p>
        
        {/* Show existing provider response */}
        {hasResponse && (
          <ReviewResponse
            provider={listingProvider}
            response={existingResponse}
          />
        )}
        
        {/* Show response form for provider (only if no existing response) */}
        {isProvider && !hasResponse && (
          showResponseForm ? (
            <ReviewResponseForm
              onSubmit={handleResponseSubmit}
              inProgress={responseSubmitInProgress}
            />
          ) : (
            <button
              className={css.responseButton}
              onClick={() => setShowResponseForm(true)}
              type="button"
            >
              {intl.formatMessage({ id: 'Reviews.respondButton' })}
            </button>
          )
        )}
      </div>
    </div>
  );
};

/**
 * A component that renders a list of reviews.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.review>} props.reviews - The reviews to render
 * @param {Object} [props.listing] - The listing entity (for provider responses)
 * @param {Object} [props.currentUser] - The current user (to check if they're the provider)
 * @param {Function} [props.onResponseSubmit] - Callback for submitting provider responses
 * @returns {JSX.Element}
 */
const Reviews = props => {
  const intl = useIntl();
  const { 
    className, 
    rootClassName, 
    reviews = [], 
    listing, 
    currentUser, 
    onResponseSubmit 
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  return reviews.length ? (
    <ul className={classes}>
      {reviews.map(r => {
        return (
          <li key={`Review_${r.id.uuid}`} className={css.reviewItem}>
            <Review 
              review={r} 
              intl={intl}
              listing={listing}
              currentUser={currentUser}
              onResponseSubmit={onResponseSubmit}
            />
          </li>
        );
      })}
    </ul>
  ) : null;
};

export default Reviews;
